import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { dateParser } from '../services/mask-date-parser';

@Component({
  standalone: false,
  selector: 'edportal-date-input',
  templateUrl: './date-input.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  styles: ``,
})
export class DateInputComponent implements OnChanges {
  @Input() model!: Date | undefined;
  @Input() name = '';
  @Input() propertyName = '';
  @Input() isDisabled = false;
  @Input() useObscurity = false;
  @Output() modelChange = new EventEmitter<Date | undefined>();

  inputString = '';
  private isNormalizing = false;

  errorEmitter = new EventEmitter<any>();

  dateParser = dateParser;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model'] && !this.isNormalizing) {
      if (this.model instanceof Date && !isNaN(this.model.getTime())) {
        // Normalize to midnight local time to strip time component
        const normalized = new Date(this.model.getFullYear(), this.model.getMonth(), this.model.getDate());
        this.inputString = this.formatDate(normalized);

        // Only emit if dates are actually different (comparing timestamps)
        // Use setTimeout to avoid change detection issues
        if (this.model.getTime() !== normalized.getTime()) {
          this.emitNormalizedDate(normalized);
        }
      } else if (typeof this.model === 'string') {
        // Handle date strings from backend (e.g., "2024-10-21T21:00:11.8570223")
        const parsed = new Date(this.model);
        if (!isNaN(parsed.getTime())) {
          const normalized = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
          this.inputString = this.formatDate(normalized);
          this.emitNormalizedDate(normalized);
        } else {
          this.inputString = '';
        }
      } else if (this.model === undefined || this.model === null) {
        this.inputString = '';
      }
    }
  }

  private emitNormalizedDate(date: Date) {
    // Use setTimeout to emit outside of the current change detection cycle
    this.isNormalizing = true;
    setTimeout(() => {
      this.modelChange.emit(date);
      this.isNormalizing = false;
    });
  }

  // Called by template when the masked string changes
  onInputChange(value: string) {
    // If input is completely empty, emit undefined to clear the date
    if (!value || value.trim() === '') {
      this.modelChange.emit(undefined);
      this.inputString = '';
      return;
    }

    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (dateOnlyRegex.test(value)) {
      // Parse as local date at midnight
      const [year, month, day] = value.split('-').map(Number);
      const d = new Date(year, month - 1, day);

      if (!isNaN(d.getTime())) {
        // Emit the date at midnight local time (no timezone conversion)
        this.modelChange.emit(d);
        this.inputString = this.formatDate(d);
        return;
      }
    }

    // Do not emit or clear anything for partial/invalid input
  }

  private formatDate(d: Date | string | undefined): string {
    if (!d) return '';

    // If it's already a Date, format directly
    if (d instanceof Date) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }

    // If it's a string (likely an ISO from mask/parser), try to parse it
    if (typeof d === 'string') {
      const parsed = new Date(d);
      if (!isNaN(parsed.getTime())) {
        const mm = String(parsed.getMonth() + 1).padStart(2, '0');
        const dd = String(parsed.getDate()).padStart(2, '0');
        const yyyy = parsed.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
      }
    }

    return '';
  }
}
